/**
 * Football 101 hub (docs/TUTORIAL.md §6.1; replaces the old intro wall of rules): the twelve lessons as broadcast
 * tiles grouped by camp (✓ done · ↷ skipped · ● up next), CONTINUE (the next lesson not done), LEARN WITH (the head
 * you learn with: lesson 10 teaches its special) and HOW TO PLAY. Every lesson is open; progress is saved
 * (ui/tutorial/progress.ts).
 *
 * Keyboard / pad: the tiles are a grid (◀ ▶ ▲ ▼ between lessons and camps); ▶ past the last tile of a row reaches
 * the side column (▲ ▼ there move between its items, ◀ goes back to the tiles); on LEARN WITH, ◀ ▶ change the head
 * and confirm does nothing (it used to cycle the head, which surprised players who meant to start). The footer
 * hints follow the focus.
 */
import '../tutorial/hub.css';
import { useMemo, useState } from 'preact/hooks';
import { CHARACTER_IDS, characterById, specialInfo } from '@hf/shared';
import { useActions, useServices } from '../actions';
import { Glove, Head, SpecialIcon } from '../components';
import { hoverArmed, useMenuLayer } from '../nav';
import { playSfx, UI_SFX } from '@hf/client-core/ui-model/sfx';
import {
  CAMPS,
  LESSONS,
  lessonTitleFor,
  type LessonId,
  type LessonInfo,
} from '@hf/client-core/ui-model/tutorial/catalog';
import {
  continueLesson,
  doneCount,
  progress,
  updateProgress,
} from '@hf/client-core/ui-model/tutorial/progress';
import { openHowToPlay } from '../tutorial/HowToPlay';
import { startTutorial } from '../tutorial/start';
import { cycle, Shell } from './Shell';

/** Rows of tiles by camp. */
const ROWS: LessonInfo[][] = CAMPS.map((c) => LESSONS.filter((l) => l.camp === c.id));
const SIDE = ['continue', 'head', 'howto'] as const;
type Focus = { kind: 'tile'; row: number; col: number } | { kind: 'side'; i: number };

const ICON_EXPR: Partial<Record<LessonId, string>> = {
  move: 'neutral',
  snap: 'determined',
  run: 'determined',
  downs: 'neutral',
  pass: 'determined',
  defense: 'determined',
  counters: 'shocked',
  fakes: 'celebrating',
  meters: 'dazed',
  scoring: 'celebrating',
  scrimmage: 'determined',
};

export function TutorialHub() {
  const actions = useActions();
  const { audio } = useServices();
  const p = progress.value;
  const head = p.head;
  const next = continueLesson(p);
  const nextInfo = next ? LESSONS.find((l) => l.id === next)! : null;
  const [focus, setFocusState] = useState<Focus>({ kind: 'side', i: 0 });
  const [lastTile, setLastTile] = useState({ row: 0, col: 0 });
  const [headNudge, setHeadNudge] = useState(0);
  const done = doneCount(p);

  const setFocus = (f: Focus): void => {
    setFocusState(f);
    if (f.kind === 'tile') setLastTile({ row: f.row, col: f.col });
    playSfx(audio, UI_SFX.move);
  };
  const setHead = (dir: -1 | 1): void => {
    updateProgress({ head: cycle(CHARACTER_IDS, head, dir) });
    playSfx(audio, UI_SFX.toggle);
  };
  const start = (id: LessonId): void => {
    playSfx(audio, UI_SFX.start);
    actions.unlockAudio();
    startTutorial(id, head, actions.startMatch);
  };
  const confirm = (f: Focus): void => {
    if (f.kind === 'tile') {
      start(ROWS[f.row]![f.col]!.id);
      return;
    }
    const s = SIDE[f.i];
    if (s === 'continue') start(next ?? 'move');
    else if (s === 'head') {
      // ◀ ▶ pick the head; confirm is not "next head" (players pressed it to start and got a different head)
      setHeadNudge((n) => n + 1);
      playSfx(audio, UI_SFX.move);
    } else {
      playSfx(audio, UI_SFX.confirm);
      openHowToPlay();
    }
  };

  useMenuLayer((a) => {
    const f = focus;
    if (a === 'back' || a === 'pause') {
      playSfx(audio, UI_SFX.back);
      actions.goto('main_menu');
      return;
    }
    if (a === 'confirm') {
      confirm(f);
      return;
    }
    if (f.kind === 'tile') {
      const row = ROWS[f.row]!;
      if (a === 'left') {
        if (f.col > 0) setFocus({ ...f, col: f.col - 1 });
      } else if (a === 'right') {
        if (f.col < row.length - 1) setFocus({ ...f, col: f.col + 1 });
        else setFocus({ kind: 'side', i: 0 });
      } else if (a === 'up' && f.row > 0) {
        setFocus({ kind: 'tile', row: f.row - 1, col: Math.min(f.col, ROWS[f.row - 1]!.length - 1) });
      } else if (a === 'down' && f.row < ROWS.length - 1) {
        setFocus({ kind: 'tile', row: f.row + 1, col: Math.min(f.col, ROWS[f.row + 1]!.length - 1) });
      }
      return;
    }
    // side column
    if (a === 'up' && f.i > 0) setFocus({ kind: 'side', i: f.i - 1 });
    else if (a === 'down' && f.i < SIDE.length - 1) setFocus({ kind: 'side', i: f.i + 1 });
    else if (SIDE[f.i] === 'head' && (a === 'left' || a === 'right')) setHead(a === 'left' ? -1 : 1);
    else if (a === 'left') setFocus({ kind: 'tile', ...lastTile });
  });

  const focusedLesson: LessonInfo | null =
    focus.kind === 'tile' ? ROWS[focus.row]![focus.col]! : focus.i === 0 ? nextInfo : null;
  const def = characterById(head);
  const sp = specialInfo(def.signature);
  const hover = (f: Focus) => ({
    onMouseEnter: () => {
      if (hoverArmed()) setFocusState(f);
    },
    onMouseMove: () => {
      if (hoverArmed()) setFocusState(f);
    },
  });
  const isFocus = (f: Focus): boolean =>
    focus.kind === 'side'
      ? f.kind === 'side' && f.i === focus.i
      : f.kind === 'tile' && f.row === focus.row && f.col === focus.col;
  const tiles = useMemo(() => ROWS, []);

  return (
    <Shell
      screen="tutorial"
      title="FOOTBALL 101"
      kicker="NEVER WATCHED A GAME? START HERE."
      class="hf-hub"
      onBack={() => actions.goto('main_menu')}
      hints={
        focus.kind === 'tile'
          ? [
              ['left|right', 'LESSON'],
              ['up|down', 'CAMP'],
              ['confirm', 'START'],
              ['back', 'BACK'],
            ]
          : SIDE[focus.i] === 'head'
            ? [
                ['left|right', 'PICK HEAD'],
                ['up|down', 'MOVE'],
                ['back', 'BACK'],
              ]
            : [
                ['up|down', 'MOVE'],
                ['left', 'LESSONS'],
                ['confirm', SIDE[focus.i] === 'continue' ? 'START' : 'OPEN'],
                ['back', 'BACK'],
              ]
      }
    >
      <div class="hf-hub__grid">
        <div class="hf-hub__camps">
          {CAMPS.map((camp, r) => (
            <section class={`hf-hub__camp ${camp.id}`} key={camp.id} aria-label={camp.title}>
              <header class="hf-hub__camphead">
                <span class="hf-hub__campname t-display">{camp.title}</span>
                <span class="hf-hub__campsub t-broadcast">{camp.sub}</span>
              </header>
              <div class="hf-hub__tiles">
                {tiles[r]!.map((l, c) => {
                  const st = p.lessons[l.id]?.status;
                  const f: Focus = { kind: 'tile', row: r, col: c };
                  const on = isFocus(f);
                  return (
                    <button
                      key={l.id}
                      class={`hf-tile interactive ${st ?? ''} ${l.id === next ? 'next' : ''} ${l.camp === 'gameday' ? 'wide' : ''}`}
                      data-focused={on}
                      data-lesson={l.id}
                      onClick={() => start(l.id)}
                      {...hover(f)}
                      aria-label={`Lesson ${l.number}: ${lessonTitleFor(l.id, head)}${st ? ` (${st})` : ''}`}
                    >
                      <span class="hf-tile__num t-display">{l.number}</span>
                      <span class="hf-tile__art" aria-hidden="true">
                        {l.id === 'special' ? (
                          <SpecialIcon special={def.signature} class="hf-tile__icon" />
                        ) : (
                          <Head
                            character={head}
                            expression={ICON_EXPR[l.id] ?? 'neutral'}
                            class="hf-tile__head"
                          />
                        )}
                      </span>
                      <span class="hf-tile__title t-broadcast">{lessonTitleFor(l.id, head)}</span>
                      {/* Game Day: finishing it is the ✓; beating the CPU is a bonus badge */}
                      {l.id === 'scrimmage' && p.lessons.scrimmage?.won ? (
                        <span class="hf-tile__win t-broadcast" aria-hidden="true">
                          ★ WON
                        </span>
                      ) : null}
                      {st === 'done' || st === 'played' ? (
                        <span class="hf-tile__badge done" aria-hidden="true">
                          ✓
                        </span>
                      ) : st === 'skipped' ? (
                        <span class="hf-tile__badge skipped" aria-hidden="true">
                          ↷
                        </span>
                      ) : l.id === next ? (
                        <span class="hf-tile__badge next t-broadcast" aria-hidden="true">
                          NEXT
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        <aside class="hf-hub__side">
          <button
            class="hf-btn hf-hub__continue interactive"
            data-focused={isFocus({ kind: 'side', i: 0 })}
            onClick={() => confirm({ kind: 'side', i: 0 })}
            {...hover({ kind: 'side', i: 0 })}
          >
            <span class="hf-hub__cta t-display">
              {done === 0 && !p.lessons.scrimmage ? 'START ROOKIE CAMP' : next ? 'CONTINUE' : 'PLAY AGAIN'}
            </span>
            <small class="t-broadcast">
              {nextInfo ? `${nextInfo.number} · ${lessonTitleFor(nextInfo.id, head)}` : 'ALL 12 LESSONS DONE'}
            </small>
          </button>
          <div
            class={`hf-hub__head hf-panel interactive ${headNudge ? 'nudge' : ''}`}
            key={`head-${headNudge}`}
            data-focused={isFocus({ kind: 'side', i: 1 })}
            onClick={() => setHead(1)}
            role="group"
            aria-label={`Learn with ${def.name}`}
            {...hover({ kind: 'side', i: 1 })}
          >
            <span class="hf-hub__label t-broadcast">LEARN WITH</span>
            <div class="hf-hub__headrow">
              <button
                class="hf-hub__arrow"
                tabIndex={-1}
                aria-label="Previous head"
                onClick={(e) => (e.stopPropagation(), setHead(-1))}
              >
                ◀
              </button>
              <div class="hf-hub__rig" key={head}>
                <Head character={head} expression="determined" class="hf-hub__headart" />
                <Glove side="home" pose="ready" class="hf-hub__glove" />
              </div>
              <button
                class="hf-hub__arrow"
                tabIndex={-1}
                aria-label="Next head"
                onClick={(e) => (e.stopPropagation(), setHead(1))}
              >
                ▶
              </button>
            </div>
            <span class="hf-hub__name t-display">{def.name}</span>
            <span class="hf-hub__special t-broadcast">
              <SpecialIcon special={def.signature} class="hf-hub__spicon" /> {sp.name}
            </span>
          </div>
          <button
            class="hf-btn dark hf-hub__howto interactive"
            data-focused={isFocus({ kind: 'side', i: 2 })}
            onClick={() => confirm({ kind: 'side', i: 2 })}
            {...hover({ kind: 'side', i: 2 })}
          >
            <span class="t-display">HOW TO PLAY</span>
            <small class="t-broadcast">RULES · CONTROLS · COUNTERS</small>
          </button>
          <div class="hf-hub__focus hf-panel" aria-live="polite">
            {focusedLesson ? (
              <>
                <span class="hf-hub__focusnum t-broadcast">
                  LESSON {focusedLesson.number} · ≈ {focusedLesson.seconds} S
                </span>
                <b class="t-display">{lessonTitleFor(focusedLesson.id, head)}</b>
                <p>
                  {focusedLesson.id === 'special'
                    ? `${sp.name}: ${sp.blurb}`
                    : focusedLesson.id === 'scrimmage' &&
                        p.lessons.scrimmage?.status === 'done' &&
                        !p.lessons.scrimmage.won
                      ? 'Done! Bonus: beat the CPU for the ★ WON badge. He eases off a little after every loss.'
                      : focusedLesson.goal}
                </p>
              </>
            ) : focus.kind === 'side' && SIDE[focus.i] === 'head' ? (
              <>
                <span class="hf-hub__focusnum t-broadcast">LEARN WITH · {def.name}</span>
                <b class="t-display">{sp.name}</b>
                <p>Lesson 10 teaches your head's special move. Use ◀ ▶ to pick another head.</p>
              </>
            ) : (
              <>
                <span class="hf-hub__focusnum t-broadcast">THE RULEBOOK</span>
                <b class="t-display">HOW TO PLAY</b>
                <p>
                  Football in eight ideas, the controls, the counter square, fakes, meters, specials and
                  scoring.
                </p>
              </>
            )}
          </div>
          <div class="hf-hub__progress" aria-label={`${done} of 12 lessons done`}>
            <span class="t-broadcast">
              {done} / {LESSONS.length} DONE
            </span>
            <i style={{ '--p': done / LESSONS.length }} />
          </div>
        </aside>
      </div>
    </Shell>
  );
}
